import React, { useEffect, useState, createElement } from 'react';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { C0, E0, EQ0, M0, ROLES0, S0, TIPOS, U0, moduleCards } from './data';
import { useStoredState } from './storage';
import { useSucursales, useUsuarios, useRoles, useConsumos, useAuditoria, useConfiguracion, guardarUsuario } from './db';
import type { AppState, Consumo, Empleado, Equipo, Medicion, Perfil, RolPermiso, Sucursal, Usuario, View } from './types';
import { Field, Modal, T, glass, inputStyle, primaryButton, ghostButton, useIsMobile, CampoValidado, useConfirmacion } from './ui';
import { Login } from './modules/Login';
import { ConsumoEnergetico } from './modules/ConsumoEnergetico';
import { Alertas } from './modules/Alertas';
import { Auditoria } from './modules/Auditoria';
import { Configuracion } from './modules/Configuracion';
import { Dashboard } from './modules/Dashboard';
import { Reportes } from './modules/Reportes';
import { RolesPermisos } from './modules/RolesPermisos';
import { Sucursales } from './modules/Sucursales';
import { Usuarios } from './modules/Usuarios';
import { ConexionSupabase } from './modules/ConexionSupabase';
import GasApp from './modules/gas';
import { Pedidos } from './modules/Pedidos';
import { Flota } from './modules/Flota';
import { useFlota, usePedidos } from './modules/gasData';
const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 5);
const stamp = () => new Date().toLocaleString('es-DO', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit'
});
const xml = (v: string | number) => String(v).replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;');
function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
type ModalKind = 'sucursal' | 'empleado' | 'medicion' | 'equipo' | 'rol' | 'usuario' | null;
type PermFlags = {
  leer: boolean;
  editar: boolean;
  archivar: boolean;
};
// Módulos actuales del sistema (los demás fueron retirados de la app).
const MODULE_KEYS: string[] = ['inicio', 'consumo', 'costoConsumo', 'sucursales', 'empleados', 'mediciones', 'equipos', 'historial', 'mapa', 'reportes', 'alertas', 'usuarios', 'roles', 'gas', 'pedidos', 'pedidosAutorizar', 'flota', 'auditoria', 'configuracion'];
const MODULE_LABELS: Record<string, string> = {
  inicio: 'Panel',
  consumo: 'Consumo',
  costoConsumo: 'Ver costo estimado',
  sucursales: 'Sucursales',
  empleados: 'Empleados',
  mediciones: 'Mediciones',
  equipos: 'Equipos',
  historial: 'Historial',
  mapa: 'Mapa',
  reportes: 'Reportes',
  alertas: 'Alertas',
  usuarios: 'Usuarios',
  roles: 'Roles y permisos',
  gas: 'Gas Industrial',
  pedidos: 'Órdenes de Pedido',
  // Permiso granular (no es una vista): habilita firmar/autorizar una orden,
  // igual que 'costoConsumo' solo destapa un dato dentro de Consumo.
  pedidosAutorizar: 'Autorizar órdenes de pedido',
  flota: 'Flota y Destinos',
  auditoria: 'Auditoría',
  configuracion: 'Configuración'
};
const emptyPermisos = (): Record<string, PermFlags> => Object.fromEntries(MODULE_KEYS.map(k => [k, {
  // El Panel arranca habilitado: es la pantalla de aterrizaje y un rol sin
  // ninguna vista permitida no tendría dónde caer. Se puede desmarcar.
  leer: k === 'inicio',
  editar: false,
  archivar: false
}]));
export default function App() {
  const isMobile = useIsMobile();
  const [dialogo, confirmar] = useConfirmacion();
  // Confirmación de alta/edición. Un solo helper para todos los módulos, así
  // el texto y el estilo son idénticos en Sucursales, Empleados, Mediciones,
  // Equipos, Roles y Usuarios en vez de repetirse seis veces.
  const confirmarGuardar = (entidad: string, esEdicion: boolean, detalles: {
    etiqueta: string;
    valor: string;
  }[]) => confirmar({
    titulo: esEdicion ? `¿Guardar los cambios?` : `¿Crear ${entidad}?`,
    mensaje: esEdicion ? `Se actualizarán los datos de ${entidad} con lo que está en el formulario.` : `Se agregará ${entidad} al sistema.`,
    detalles: detalles.filter(d => d.valor),
    confirmar: esEdicion ? 'Guardar cambios' : 'Crear',
    tono: 'accent'
  });

  // Confirmación de archivado/desarchivado, también compartida.
  const confirmarArchivar = (entidad: string, nombre: string, archivando: boolean) => confirmar({
    titulo: archivando ? `¿Archivar ${entidad}?` : `¿Desarchivar ${entidad}?`,
    mensaje: archivando ? 'Sale del listado principal y queda en la pestaña "Archivados". No se borra nada y se puede revertir.' : 'Vuelve al listado principal.',
    detalles: [{
      etiqueta: 'Registro',
      valor: nombre
    }],
    confirmar: archivando ? 'Sí, archivar' : 'Sí, desarchivar',
    tono: archivando ? 'warn' : 'accent'
  });

  // Reemplazo de alert(): mismo diálogo del sistema, con un solo botón.
  const avisar = (titulo: string, mensaje?: string) => {
    void confirmar({
      titulo,
      mensaje,
      soloAceptar: true,
      tono: 'warn'
    });
  };
  // Entidades sincronizadas con tablas reales de Supabase (caché local de respaldo)
  const [sucs, setSucs] = useSucursales(S0);
  const [usuarios, setUsuarios] = useUsuarios(U0);
  const [roles, setRoles] = useRoles(ROLES0);
  const [consumos, setConsumos] = useConsumos();
  const {
    pedidos,
    onAddPedido,
    onUpdatePedido,
    onDeletePedido
  } = usePedidos();
  const flota = useFlota();
  const {
    audit,
    log
  } = useAuditoria(stamp);
  const [session, setSession] = useStoredState<Usuario | null>('getr.session', null);
  // El usuario logueado es exactamente lo que devolvió la Edge Function
  // 'login' (sin password). Ya no se busca en el array local `usuarios`:
  // ese array puede no incluir a este usuario si está filtrado/paginado,
  // y además así evitamos depender de tener la lista completa cargada.
  const sessionUser = session;
  const perfil: Perfil = {
    nombre: sessionUser?.nombre ?? 'Usuario',
    correo: sessionUser?.correo ?? '',
    rol: sessionUser?.rol ?? 'tecnico'
  };
  const {
    notif,
    prefs,
    setNotif,
    setPrefs
  } = useConfiguracion({
    perfil,
    notif: {
      fueraRango: true,
      resumen: true,
      auditoria: false,
      email: true
    },
    prefs: {
      luxMin: 250,
      idioma: 'Español',
      zona: 'America/Santo_Domingo',
      tema: 'Oscuro'
    }
  }, session?.id ?? 'guest');
  // Al editar el perfil (nombre/correo) se actualiza vía la Edge Function
  // 'guardar-usuario' (no hay escritura directa a la tabla `usuarios`) y se
  // refleja también en la sesión local para que la UI quede sincronizada.
  const setPerfil = async (value: Perfil) => {
    if (!session) return;
    const correoNuevo = value.correo.trim().toLowerCase();
    const enUsoPorOtro = usuarios.some(u => u.id !== session.id && u.correo.trim().toLowerCase() === correoNuevo);
    if (correoNuevo && enUsoPorOtro) {
      avisar('Ese correo ya está en uso', 'Otro usuario del sistema ya tiene registrado ese correo.');
      return;
    }
    const {
      ok,
      error
    } = await guardarUsuario({
      id: session.id,
      nombre: value.nombre,
      correo: value.correo,
      rol: session.rol,
      sucursalesIds: session.sucursalesIds,
      archivado: session.archivado
    });
    if (!ok) {
      avisar('No se pudo actualizar el perfil', error);
      return;
    }
    setSession({
      ...session,
      nombre: value.nombre,
      correo: value.correo
    });
    setUsuarios(prev => prev.map(u => u.id === session.id ? {
      ...u,
      nombre: value.nombre,
      correo: value.correo
    } : u));
    log(`Perfil actualizado: ${value.nombre} (${value.correo})`);
  };
  // Aún en localStorage (no forman parte del modelo de tablas pedido)
  const [emps, setEmps] = useStoredState<Empleado[]>('getr.empleados', E0);
  const [meds, setMeds] = useStoredState<Medicion[]>('getr.mediciones', M0);
  const [equipos, setEquipos] = useStoredState<Equipo[]>('getr.equipos', EQ0);
  const [dismissedAlerts, setDismissedAlerts] = useStoredState<string[]>('getr.alertas.resueltas', []);
  const [view, setView] = useState<View>('inicio');
  const [scopeSucId, setScopeSucId] = useState<string>('');
  const [modal, setModal] = useState<ModalKind>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  // Actualizar el título de la pestaña del navegador
  useEffect(() => {
    const moduleName = view === 'inicio' ? 'Panel' : MODULE_LABELS[view] || view;
    document.title = `ELECTRICIDAD | ${moduleName}`;
  }, [view]);
  // form state
  const [sN, setSN] = useState('');
  const [sD, setSD] = useState('');
  const [sT, setST] = useState(TIPOS[0]);
  const [eN, setEN] = useState('');
  const [eC, setEC] = useState('');
  const [eS, setES] = useState('');
  const [mS, setMS] = useState('');
  const [mP, setMP] = useState('');
  const [mL, setML] = useState('');
  const [mE, setME] = useState('');
  const [mN, setMN] = useState('');
  const [eqNombre, setEqNombre] = useState('');
  const [eqSerial, setEqSerial] = useState('');
  const [eqEstado, setEqEstado] = useState('Disponible');
  const [eqCal, setEqCal] = useState('');
  const [rolNombre, setRolNombre] = useState('');
  const [rolDesc, setRolDesc] = useState('');
  const [rolPermisos, setRolPermisos] = useState<Record<string, PermFlags>>(emptyPermisos());
  const [uN, setUN] = useState('');
  const [uCorreo, setUCorreo] = useState('');
  const [uPass, setUPass] = useState('');
  const [showUPass, setShowUPass] = useState(false);
  const [uRol, setURol] = useState<Usuario['rol']>('tecnico');
  const [uSucs, setUSucs] = useState<string[]>([]);
  const state: AppState = {
    sucs,
    emps,
    meds,
    perfil,
    notif,
    prefs,
    audit,
    dismissedAlerts,
    equipos,
    roles,
    usuarios,
    consumos
  };
  // ── SESSION / SCOPING ─────────────────────────────────────────────
  const currentUser = session;
  if (!currentUser) {
    return <Login onLogin={usuario => {
      setSession(usuario);
      // Siempre al Panel: es la única vista garantizada para todo rol. Antes
      // los no-admin aterrizaban en Consumo aunque su rol no lo permitiera.
      setView('inicio');
    }} data-id="element-0" data-mp="2k56nn6puheir" />;
  }
  // Sesiones persistidas por versiones anteriores podían no traer
  // `sucursalesIds`; normalizamos a un arreglo para no romper el render.
  const currentSucursalesIds = Array.isArray(currentUser.sucursalesIds) ? currentUser.sucursalesIds : [];
  // OJO: estos dos conceptos son distintos y antes estaban mezclados en una
  // sola variable `isAdmin`, lo que causaba que CUALQUIER usuario sin
  // sucursales asignadas (el valor por defecto al crear uno nuevo) recibiera
  // acceso total como administrador, sin importar los permisos reales de su
  // rol. Ahora:
  // - `isAdmin`: solo es true para el rol 'admin' real. Controla el bypass
  //   de permisos (canRead/canEdit/canArchive) y el menú completo.
  // - `seesAllSucursales`: true para admin O para cualquier usuario cuyo
  //   `sucursalesIds` esté vacío (convención: [] = "todas"). Solo controla
  //   qué datos/sucursales ve, no qué puede hacer con ellos.
  const isAdmin = currentUser.rol === 'admin';
  const seesAllSucursales = isAdmin || currentSucursalesIds.length === 0;
  const scopeSucursales = seesAllSucursales ? sucs : sucs.filter(s => currentSucursalesIds.includes(s.id));
  // Siempre se ofrece el selector de sucursal en Consumo, aunque solo haya
  // una sucursal disponible (definitivo, a petición del usuario).
  const puntoFijo = false;
  const activeSuc = scopeSucursales.find(s => s.id === scopeSucId) ?? scopeSucursales[0] ?? sucs[0];
  const logout = () => {
    setSession(null);
    setView('inicio');
  };
  // ── HANDLERS (admin entity CRUD) ──────────────────────────────────
  const openNew = (target: Exclude<ModalKind, null>) => {
    setEditingId(null);
    if (target === 'sucursal') {
      setSN('');
      setSD('');
      setST(TIPOS[0]);
    }
    if (target === 'empleado') {
      setEN('');
      setEC('');
      setES('');
    }
    if (target === 'medicion') {
      setMS('');
      setMP('');
      setML('');
      setME('');
      setMN('');
    }
    if (target === 'equipo') {
      setEqNombre('');
      setEqSerial('');
      setEqEstado('Disponible');
      setEqCal('');
    }
    if (target === 'rol') {
      setRolNombre('');
      setRolDesc('');
      setRolPermisos(emptyPermisos());
    }
    if (target === 'usuario') {
      setUN('');
      setUCorreo('');
      setUPass('');
      setShowUPass(false);
      const primerRol = roles[0]?.id.replace(/^rol-/, '') as Usuario['rol'];
      setURol(primerRol ?? 'tecnico');
      setUSucs([]);
    }
    setModal(target);
  };
  const editSuc = (id: string) => {
    const s = sucs.find(x => x.id === id);
    if (!s) return;
    setEditingId(id);
    setSN(s.nombre);
    setSD(s.direccion);
    setST(s.tipo);
    setModal('sucursal');
  };
  const editRol = (id: string) => {
    const r = roles.find(x => x.id === id);
    if (!r) return;
    setEditingId(id);
    setRolNombre(r.nombre);
    setRolDesc(r.descripcion);
    setRolPermisos({
      ...emptyPermisos(),
      ...r.permisos
    });
    setModal('rol');
  };
  const editUsuario = (id: string) => {
    const u = usuarios.find(x => x.id === id);
    if (!u) return;
    setEditingId(id);
    setUN(u.nombre);
    setUCorreo(u.correo);
    // El campo queda vacío a propósito: la contraseña actual NUNCA llega al
    // cliente. Si se deja vacío al guardar, el servidor conserva el hash actual.
    setUPass('');
    setShowUPass(false);
    setURol(u.rol);
    setUSucs(u.sucursalesIds);
    setModal('usuario');
  };
  const addSuc = async () => {
    if (!sN.trim()) {
      avisar('Ingresa el nombre');
      return;
    }
    if (!(await confirmarGuardar('la sucursal', !!editingId, [{
      etiqueta: 'Nombre',
      valor: sN
    }, {
      etiqueta: 'Dirección',
      valor: sD
    }, {
      etiqueta: 'Tipo',
      valor: sT
    }]))) return;
    if (editingId) {
      setSucs(sucs.map(s => s.id === editingId ? {
        ...s,
        nombre: sN,
        direccion: sD,
        tipo: sT
      } : s));
      log(`Sucursal editada: ${sN}`);
    } else {
      setSucs([...sucs, {
        id: uid(),
        nombre: sN,
        direccion: sD,
        tipo: sT,
        creado: Date.now()
      }]);
      log(`Sucursal creada: ${sN}`);
    }
    setEditingId(null);
    setModal(null);
  };
  const addEmp = async () => {
    if (!eN.trim()) {
      avisar('Ingresa el nombre');
      return;
    }
    if (!(await confirmarGuardar('el empleado', !!editingId, [{
      etiqueta: 'Nombre',
      valor: eN
    }, {
      etiqueta: 'Cargo',
      valor: eC
    }, {
      etiqueta: 'Sucursal',
      valor: sucs.find(x => x.id === eS)?.nombre ?? ''
    }]))) return;
    if (editingId) {
      setEmps(emps.map(e => e.id === editingId ? {
        ...e,
        nombre: eN,
        cargo: eC,
        sucursalId: eS
      } : e));
      log(`Empleado editado: ${eN}`);
    } else {
      setEmps([...emps, {
        id: uid(),
        nombre: eN,
        cargo: eC,
        sucursalId: eS
      }]);
      log(`Empleado creado: ${eN}`);
    }
    setEditingId(null);
    setModal(null);
  };
  const addMed = async () => {
    const lux = parseFloat(mL);
    if (!mS) {
      avisar('Selecciona sucursal');
      return;
    }
    if (!mP.trim()) {
      avisar('Ingresa el punto');
      return;
    }
    if (Number.isNaN(lux) || lux < 0) {
      avisar('Lux invalido');
      return;
    }
    if (!(await confirmarGuardar('la medición', !!editingId, [{
      etiqueta: 'Sucursal',
      valor: sucs.find(x => x.id === mS)?.nombre ?? ''
    }, {
      etiqueta: 'Punto',
      valor: mP
    }, {
      etiqueta: 'Lux',
      valor: String(lux)
    }]))) return;
    if (editingId) {
      setMeds(meds.map(m => m.id === editingId ? {
        ...m,
        sucursalId: mS,
        punto: mP,
        lux,
        empleadoId: mE,
        notas: mN
      } : m));
      log(`Medicion editada: ${mP}`);
    } else {
      setMeds([{
        id: uid(),
        sucursalId: mS,
        punto: mP,
        lux,
        empleadoId: mE,
        notas: mN,
        fecha: Date.now()
      }, ...meds]);
      log(`Medicion registrada: ${mP}`);
    }
    setEditingId(null);
    setModal(null);
  };
  const addEquipo = async () => {
    if (!eqNombre.trim()) {
      avisar('Ingresa el nombre del equipo');
      return;
    }
    if (!(await confirmarGuardar('el equipo', !!editingId, [{
      etiqueta: 'Nombre',
      valor: eqNombre
    }, {
      etiqueta: 'Serial',
      valor: eqSerial
    }, {
      etiqueta: 'Estado',
      valor: eqEstado
    }]))) return;
    if (editingId) {
      setEquipos(equipos.map(q => q.id === editingId ? {
        ...q,
        nombre: eqNombre,
        serial: eqSerial,
        estado: eqEstado,
        calibracion: eqCal
      } : q));
      log(`Equipo editado: ${eqNombre}`);
    } else {
      setEquipos([...equipos, {
        id: uid(),
        nombre: eqNombre,
        serial: eqSerial,
        estado: eqEstado,
        calibracion: eqCal
      }]);
      log(`Equipo creado: ${eqNombre}`);
    }
    setEditingId(null);
    setModal(null);
  };
  const addRol = async () => {
    if (!rolNombre.trim()) {
      avisar('Ingresa el nombre del rol');
      return;
    }
    if (!(await confirmarGuardar('el rol', !!editingId, [{
      etiqueta: 'Nombre',
      valor: rolNombre
    }, {
      etiqueta: 'Descripción',
      valor: rolDesc
    }, {
      etiqueta: 'Módulos con lectura',
      valor: String(Object.values(rolPermisos).filter(x => x.leer).length)
    }]))) return;
    if (editingId) {
      setRoles(roles.map(r => r.id === editingId ? {
        ...r,
        nombre: rolNombre,
        descripcion: rolDesc,
        permisos: rolPermisos
      } : r));
      log(`Rol editado: ${rolNombre}`);
    } else {
      setRoles([...roles, {
        id: uid(),
        nombre: rolNombre,
        descripcion: rolDesc,
        permisos: rolPermisos
      }]);
      log(`Rol creado: ${rolNombre}`);
    }
    setEditingId(null);
    setModal(null);
  };
  const togglePerm = (modulo: string, accion: keyof PermFlags) => {
    setRolPermisos(prev => {
      const cur = prev[modulo] || {
        leer: false,
        editar: false,
        archivar: false
      };
      return {
        ...prev,
        [modulo]: {
          ...cur,
          [accion]: !cur[accion]
        }
      };
    });
  };
  const toggleUSuc = (id: string) => setUSucs(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const addUsuario = async () => {
    if (!uN.trim()) {
      avisar('Ingresa el nombre');
      return;
    }
    if (!uCorreo.trim()) {
      avisar('Ingresa el correo');
      return;
    }
    if (!editingId && !uPass.trim()) {
      {
        avisar('Ingresa una contraseña para el nuevo usuario.');
        return;
      }
    }
    if (uPass.trim() && uPass.trim().length < 6) {
      {
        avisar('La contraseña debe tener al menos 6 caracteres.');
        return;
      }
    }
    const sucsIds = uRol === 'admin' ? [] : uSucs;
    if (!(await confirmarGuardar('el usuario', !!editingId, [{
      etiqueta: 'Nombre',
      valor: uN
    }, {
      etiqueta: 'Correo',
      valor: uCorreo
    }, {
      etiqueta: 'Rol',
      valor: roles.find(r => r.id === uRol || r.id === `rol-${uRol}`)?.nombre ?? uRol
    }, {
      etiqueta: 'Sucursales',
      valor: sucsIds.length ? String(sucsIds.length) : 'Todas'
    }]))) return;
    const id = editingId ?? uid();
    const {
      ok,
      error
    } = await guardarUsuario({
      id,
      nombre: uN,
      correo: uCorreo,
      password: uPass.trim() || undefined,
      rol: uRol,
      sucursalesIds: sucsIds
    });
    if (!ok) {
      avisar('No se pudo guardar el usuario', error);
      return;
    }
    if (editingId) {
      setUsuarios(usuarios.map(u => u.id === editingId ? {
        ...u,
        nombre: uN,
        correo: uCorreo,
        rol: uRol,
        sucursalesIds: sucsIds
      } : u));
      // Si te estás editando a ti mismo, la sesión también tiene que
      // actualizarse: la cabecera, el "AUTORIZADO POR" de los pedidos y el
      // alcance por sucursal salen de ahí, no de la lista de usuarios. Antes
      // seguías viendo tu nombre viejo hasta cerrar sesión.
      if (session && session.id === editingId) {
        setSession({
          ...session,
          nombre: uN,
          correo: uCorreo,
          rol: uRol,
          sucursalesIds: sucsIds
        });
      }
      log(`Usuario editado: ${uN}`);
    } else {
      setUsuarios([...usuarios, {
        id,
        nombre: uN,
        correo: uCorreo,
        rol: uRol,
        sucursalesIds: sucsIds
      }]);
      log(`Usuario creado: ${uN}`);
    }
    setEditingId(null);
    setModal(null);
  };
  const deleteSuc = async (id: string) => {
    const suc = sucs.find(s => s.id === id);
    if (!suc) return;
    const archivado = !suc.archivado;
    if (!(await confirmarArchivar('esta sucursal', suc.nombre, archivado))) return;
    setSucs(sucs.map(s => s.id === id ? {
      ...s,
      archivado
    } : s));
    log(`Sucursal ${archivado ? 'archivada' : 'desarchivada'}: ${suc.nombre}`);
  };
  const deleteRol = async (id: string) => {
    const rol = roles.find(x => x.id === id);
    if (!rol) return;
    const archivado = !rol.archivado;
    if (!(await confirmarArchivar('este rol', rol.nombre, archivado))) return;
    setRoles(roles.map(x => x.id === id ? {
      ...x,
      archivado
    } : x));
    log(`Rol ${archivado ? 'archivado' : 'desarchivado'}: ${rol.nombre}`);
  };
  // Archivar un usuario TIENE que pasar por la Edge Function: la tabla
  // `usuarios` tiene RLS estricto y el cliente anónimo no puede escribirla.
  // Antes esto solo tocaba el estado local, así que el cambio se perdía en
  // la siguiente carga, cuando 'listar-usuarios' vuelve a traer la lista del
  // servidor y la sobrescribe. Por eso "no funcionaba el archivado".
  const deleteUsuario = async (id: string) => {
    const u = usuarios.find(x => x.id === id);
    if (!u) return;
    const archivado = !u.archivado;
    if (!(await confirmarArchivar('este usuario', u.nombre, archivado))) return;
    const {
      ok,
      error
    } = await guardarUsuario({
      id: u.id,
      nombre: u.nombre,
      correo: u.correo,
      rol: u.rol,
      sucursalesIds: u.sucursalesIds,
      archivado
    });
    if (!ok) {
      avisar('No se pudo archivar el usuario', error);
      return;
    }
    setUsuarios(usuarios.map(x => x.id === id ? {
      ...x,
      archivado
    } : x));
    log(`Usuario ${archivado ? 'archivado' : 'desarchivado'}: ${u.nombre}`);
  };
  const deleteConsumo = async (id: string) => {
    const c = consumos.find(x => x.id === id);
    if (!c) return;
    const archivado = !c.archivado;
    const nombreSuc = sucs.find(su => su.id === c.sucursalId)?.nombre ?? c.sucursalId;
    if (!(await confirmarArchivar('esta lectura', `${nombreSuc} — ${c.kwh} kWh`, archivado))) return;
    setConsumos(consumos.map(x => x.id === id ? {
      ...x,
      archivado
    } : x));
    const nombre = sucs.find(su => su.id === c.sucursalId)?.nombre ?? c.sucursalId;
    log(`Consumo ${archivado ? 'archivado' : 'desarchivado'} en ${nombre}: ${c.kwh} kWh`);
  };
  const addConsumo = (sucursalId: string, fecha: number, kwh: number) => {
    setConsumos([...consumos, {
      id: uid(),
      sucursalId,
      fecha,
      kwh
    }]);
    const nombre = sucs.find(s => s.id === sucursalId)?.nombre ?? sucursalId;
    log(`Consumo registrado en ${nombre}: ${kwh} kWh`);
  };
  // ── PERMISOS DEL ROL ──────────────────────────────────────────────
  // El rol del usuario se mapea a su definición en `roles` para saber qué
  // módulos puede leer. Los roles "semilla" (admin/supervisor/tecnico)
  // guardan su id como 'rol-<rol>', pero los roles personalizados creados
  // desde el módulo "Roles y permisos" guardan su id tal cual (sin prefijo),
  // y eso es justamente lo que se guarda en `usuario.rol`. Por eso probamos
  // primero el id exacto y, si no existe, probamos con el prefijo 'rol-'.
  const userRole = roles.find(r => r.id === currentUser.rol) ?? roles.find(r => r.id === `rol-${currentUser.rol}`);
  const permisos = userRole?.permisos ?? {};
  // Solo algunos roles ven el costo estimado en Consumo (permiso 'costoConsumo').
  const canSeeCosto = isAdmin || !!permisos.costoConsumo?.leer;
  // Firmar una orden de pedido es una atribución aparte de poder crearla: un
  // despachador arma el borrador, pero solo quien tiene 'pedidosAutorizar'
  // estampa su nombre en el "AUTORIZADO POR" del formulario.
  const canAutorizarPedidos = isAdmin || !!permisos.pedidosAutorizar?.leer;
  // Todos los módulos, incluido el Panel, pasan por el permiso del rol.
  // Excepción de compatibilidad: los roles creados antes de que 'inicio'
  // fuera otorgable no lo tienen guardado, y quedarse sin Panel de golpe los
  // dejaría sin pantalla de aterrizaje. Por eso ausente = permitido, y solo
  // un `false` explícito lo niega.
  const canRead = (id: View) => {
    if (isAdmin) return true;
    if (id === 'inicio') return permisos.inicio?.leer !== false;
    return !!permisos[id]?.leer;
  };
  const canEdit = (id: View) => isAdmin || !!permisos[id]?.editar;
  const canArchive = (id: View) => isAdmin || !!permisos[id]?.archivar;
  // ── NAV ───────────────────────────────────────────────────────────
  // Módulos visibles del producto (los demás fueron retirados de la app).
  const ALLOWED_NAV_KEYS: string[] = ['sucursales', 'reportes', 'alertas', 'gas', 'pedidos', 'flota', 'auditoria', 'configuracion'];
  // Orden en que se muestran las vistas y en que se busca un respaldo cuando
  // la activa no está permitida.
  const ORDEN_VISTAS: View[] = ['inicio', 'consumo', ...(ALLOWED_NAV_KEYS as View[])];
  // Una sola construcción para admin y no-admin: el bypass del admin ya está
  // dentro de canRead.
  const nav: {
    id: View;
    label: string;
  }[] = ORDEN_VISTAS.filter(canRead).map(v => ({
    id: v,
    label: MODULE_LABELS[v] ?? v
  }));
  // Primera vista que el rol sí puede abrir. Puede no haber ninguna si le
  // quitaron todos los permisos, incluido el Panel.
  const primeraVista = ORDEN_VISTAS.find(canRead);

  // El usuario solo ve y elige entre las sucursales que tiene asignadas
  // (admin = todas). `scopeSucursales` y `activeSuc` ya respetan ese alcance.
  const consumoScreen = activeSuc ? <ConsumoEnergetico state={state} sucursal={activeSuc} sucursalesDisponibles={scopeSucursales} puntoFijo={puntoFijo} mostrarCosto={canSeeCosto} onChangeSucursal={setScopeSucId} onSave={addConsumo} onDelete={canArchive('consumo') ? deleteConsumo : undefined} canArchive={canArchive('consumo')} data-id="element-1" data-mp="2od46zow1rlye" /> : <div style={{
    color: T.textMuted,
    padding: 40,
    textAlign: 'center'
  }} data-id="element-2" data-mp="3gzm3g3sf5bl7">
    
      No tienes ningún punto comercial asignado.
    </div>;

  // ── ALCANCE DE DATOS ──────────────────────────────────────────────
  // Para usuarios no-admin, todas las vistas se limitan a sus sucursales
  // asignadas. El admin (sucursalesIds vacío) ve todo.
  const viewSucs = seesAllSucursales ? sucs : scopeSucursales;
  const scopeIds = new Set(scopeSucursales.map(s => s.id));
  const viewMeds = seesAllSucursales ? meds : meds.filter(m => scopeIds.has(m.sucursalId));
  const viewEmps = seesAllSucursales ? emps : emps.filter(e => scopeIds.has(e.sucursalId));
  const viewConsumos = seesAllSucursales ? consumos : consumos.filter(c => scopeIds.has(c.sucursalId));
  const viewState: AppState = seesAllSucursales ? state : {
    ...state,
    sucs: viewSucs,
    meds: viewMeds,
    emps: viewEmps,
    consumos: viewConsumos
  };
  const noop = () => {};
  const pages: Record<string, React.ReactNode> = {
    consumo: consumoScreen,
    inicio: <Dashboard state={viewState} setView={setView} data-id="element-3" data-mp="2uqyur36rls0t" />,
    sucursales: <Sucursales sucs={viewSucs} onNew={canEdit('sucursales') ? () => openNew('sucursal') : undefined} onEdit={canEdit('sucursales') ? editSuc : undefined} onDelete={canArchive('sucursales') ? deleteSuc : undefined} canArchive={canArchive('sucursales')} data-id="element-4" data-mp="2uri43vicn838" />,
    gas: <GasApp data-id="element-5" data-mp="2grfmmadyegoi" />,
    pedidos: <Pedidos pedidos={pedidos} onAdd={onAddPedido} onUpdate={onUpdatePedido} onDelete={onDeletePedido} conductores={flota.conductores} envasadoras={flota.envasadoras} puedeVerFlota={canRead('flota')} canEdit={canEdit('pedidos')} canArchive={canArchive('pedidos')} canAutorizar={canAutorizarPedidos} usuarioNombre={perfil.nombre} usuarioCargo={userRole?.nombre ?? currentUser.rol} onLog={log} data-id="element-6" data-mp="2grz4jr3lt52j" />,
    flota: <Flota camiones={flota.camiones} colas={flota.colas} envasadoras={flota.envasadoras} conductores={flota.conductores} onAddCamion={flota.onAddCamion} onUpdateCamion={flota.onUpdateCamion} onDeleteCamion={flota.onDeleteCamion} onAddCola={flota.onAddCola} onUpdateCola={flota.onUpdateCola} onDeleteCola={flota.onDeleteCola} onAddEnvasadora={flota.onAddEnvasadora} onUpdateEnvasadora={flota.onUpdateEnvasadora} onDeleteEnvasadora={flota.onDeleteEnvasadora} onAddConductor={flota.onAddConductor} onUpdateConductor={flota.onUpdateConductor} onDeleteConductor={flota.onDeleteConductor} canEdit={canEdit('flota')} canArchive={canArchive('flota')} onLog={log} data-id="element-7" data-mp="1x1wwrc9gz4iw" />,
    reportes: <Reportes state={viewState} onLog={log} data-id="element-8" data-mp="3pzix3rqdqgtg" />,
    alertas: <Alertas state={viewState} resolveAlert={id => {
      setDismissedAlerts([...dismissedAlerts, id]);
      log('Alerta resuelta');
    }} data-id="element-9" data-mp="3pxzh828my7s7" />,
    auditoria: <Auditoria state={state} data-id="element-10" data-mp="3lj1fdyvpu22" />,
    configuracion: <Configuracion state={state} setPerfil={setPerfil} setNotif={setNotif} setPrefs={setPrefs} usuariosNode={canRead('usuarios') ? <Usuarios state={state} onNew={canEdit('usuarios') ? () => openNew('usuario') : noop} onEdit={canEdit('usuarios') ? editUsuario : noop} onDelete={canArchive('usuarios') ? deleteUsuario : noop} canArchive={canArchive('usuarios')} data-id="element-12" data-mp="379az1o1jegk4" /> : undefined} rolesNode={canRead('roles') ? <RolesPermisos roles={roles} onNew={canEdit('roles') ? () => openNew('rol') : noop} onEdit={canEdit('roles') ? editRol : noop} onDelete={canArchive('roles') ? deleteRol : noop} canArchive={canArchive('roles')} data-id="element-13" data-mp="2lwdxn8uecfuv" /> : undefined} conexionNode={isAdmin ? <ConexionSupabase data-id="element-14" data-mp="1y84gwlc1iial" /> : undefined} data-id="element-11" data-mp="3mka5bsfa4gm" />
  };
  // Si la vista activa no está permitida se cae a la primera que sí lo esté.
  // Si el rol no tiene ninguna, se dice claramente en vez de mostrar una
  // pantalla en blanco o colar un módulo negado.
  const sinAcceso = <div style={{
    ...glass,
    padding: 40,
    textAlign: 'center',
    color: T.textSecondary,
    maxWidth: 520,
    margin: '40px auto'
  }} data-id="element-15" data-mp="1ta2mx5d2sykc">
      <div style={{
      fontSize: 17,
      fontWeight: 800,
      color: T.text,
      marginBottom: 8
    }} data-id="element-16" data-mp="1fbmjbnaifo6f">
        Sin módulos habilitados
      </div>
      Tu rol no tiene permiso de lectura sobre ningún módulo. Pídele a un
      administrador que te habilite al menos uno en Roles y permisos.
    </div>;
  const page = canRead(view) ? pages[view] : primeraVista ? pages[primeraVista] : sinAcceso;
  return <div style={{
    minHeight: '100vh',
    fontFamily: "'DM Sans',-apple-system,sans-serif",
    background: '#FFFCF8',
    color: '#1A1207'
  }} data-id="element-17" data-mp="19pu7lz6ha0jw">
      
      <header style={{
      ...glass,
      position: 'sticky',
      top: 0,
      zIndex: 100,
      borderRadius: 0,
      borderLeft: 'none',
      borderRight: 'none',
      borderTop: 'none',
      padding: isMobile ? '10px 14px' : '12px 24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: isMobile ? 'wrap' : 'nowrap',
      gap: isMobile ? 10 : 18
    }} data-id="element-18" data-mp="1jlxetjg6spdp">
        
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexShrink: 0
      }} data-id="element-19" data-mp="22dhph6gd6d2u">
          
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/3naxhgyiiyi6ejjvg5a7v7/cd1b6093-bb50-47fd-9201-500f55ec0a85/pasted-image.png" alt="Junquito Gas" style={{
          height: isMobile ? 38 : 96,
          width: 'auto',
          maxWidth: isMobile ? '58vw' : 'none',
          objectFit: 'contain',
          display: 'block'
        }} data-id="element-20" data-mp="v0fp8lv1s2qs" />
          
        </div>
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? 8 : 12,
        flexShrink: 0,
        marginLeft: isMobile ? 'auto' : 0,
        order: isMobile ? 1 : 3
      }} data-id="element-21" data-mp="1dr75869jire5">
          
          <div style={{
          textAlign: 'right',
          lineHeight: 1.25
        }} data-id="element-22" data-mp="1nq644x4i78ny">
            
            <div style={{
            fontSize: 12.5,
            fontWeight: 700,
            color: '#1A1207'
          }} data-id="element-23" data-mp="1rpoljix491m1">
              
              {currentUser.nombre}
            </div>
            <div style={{
            fontSize: 11,
            color: T.textMuted,
            textTransform: 'capitalize'
          }} data-id="element-24" data-mp="3tj4g2qwbr0ma">
              
              {userRole?.nombre ?? currentUser.rol}
            </div>
          </div>
          <button onClick={logout} style={{
          ...ghostButton,
          padding: '7px 12px'
        }} data-id="element-25" data-mp="3h5qx06n64fsg">
            
            Salir
          </button>
        </div>
        <nav style={{
        display: 'flex',
        gap: isMobile ? 6 : 4,
        flexWrap: isMobile ? 'nowrap' : 'wrap',
        justifyContent: isMobile ? 'flex-start' : 'center',
        order: isMobile ? 3 : 2,
        width: isMobile ? '100%' : 'auto',
        overflowX: isMobile ? 'auto' : 'visible',
        WebkitOverflowScrolling: 'touch',
        marginTop: isMobile ? 4 : 0,
        paddingTop: isMobile ? 8 : 0,
        paddingBottom: isMobile ? 2 : 0,
        borderTop: isMobile ? `1px solid ${T.border}` : 'none'
      }} data-id="element-26" data-mp="388gpmwdh1ast">
          
          {nav.map(item => {
          const active = view === item.id;
          return <button key={item.id} onClick={() => setView(item.id)} style={{
            padding: '7px 12px',
            borderRadius: 10,
            border: 'none',
            background: active ? 'rgba(255,107,1,.12)' : 'transparent',
            color: active ? T.accent : T.textMuted,
            fontSize: 12.5,
            fontWeight: active ? 700 : 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }} data-id="element-27" data-mp="2fkukhjolhy3u">
                
                {item.label}
              </button>;
        })}
        </nav>
      </header>

      <main style={{
      padding: isMobile ? '20px 14px 48px' : '32px 24px 60px',
      maxWidth: 1160,
      margin: '0 auto'
    }} data-id="element-28" data-mp="uyl3zyj5gsfu">
        
        {page}
      </main>

      {modal === 'sucursal' && <Modal title={editingId ? 'Editar sucursal' : 'Nueva sucursal'} onClose={() => {
      setModal(null);
      setEditingId(null);
    }} data-id="element-29" data-mp="lwv19wjti72r">
        
          <Field label="NOMBRE" data-id="element-30" data-mp="36pgeu0470lky">
            <CampoValidado modo="letras" valor={sN} onChange={setSN} placeholder="Ej: Plaza Central" data-id="element-31" data-mp="36qi1z2yhgyj1" />
          
          </Field>
          <Field label="DIRECCION" data-id="element-32" data-mp="2y9r71th1e656">
            <input style={inputStyle} value={sD} onChange={e => setSD(e.target.value)} placeholder="Calle, ciudad" data-id="element-33" data-mp="2u72vvf4uon51" />
          
          </Field>
          <Field label="TIPO" data-id="element-34" data-mp="385awbx13i6ky">
            <select style={inputStyle} value={sT} onChange={e => setST(e.target.value)} data-id="element-35" data-mp="33794amloywxc">
            
              {TIPOS.map(t => <option key={t} data-id="element-36" data-mp="2fgn93vwsd74r">{t}</option>)}
            </select>
          </Field>
          <button onClick={addSuc} style={{
        ...primaryButton,
        width: '100%'
      }} data-id="element-37" data-mp="2ailp98rjd3s8">
          
            {editingId ? 'Guardar cambios' : 'Guardar'}
          </button>
        </Modal>}
      {modal === 'empleado' && <Modal title={editingId ? 'Editar empleado' : 'Nuevo empleado'} onClose={() => {
      setModal(null);
      setEditingId(null);
    }} data-id="element-38" data-mp="2kfgo5srq1671">
        
          <Field label="NOMBRE" data-id="element-39" data-mp="1wokoyakxr8pr">
            <CampoValidado modo="letras" valor={eN} onChange={setEN} data-id="element-40" data-mp="1rsm6f3magkx9" />
          
          </Field>
          <Field label="CARGO" data-id="element-41" data-mp="3ruvh6th1srh9">
            <CampoValidado modo="letras" valor={eC} onChange={setEC} data-id="element-42" data-mp="3rtkaolw7x5mg" />
          
          </Field>
          <Field label="SUCURSAL" data-id="element-43" data-mp="m7v2bazh08n">
            <select style={inputStyle} value={eS} onChange={e => setES(e.target.value)} data-id="element-44" data-mp="5ehfjbtbx83j">
            
              <option value="" data-id="element-45" data-mp="9g2xvcomuel4">Sin asignar</option>
              {sucs.map(s => <option key={s.id} value={s.id} data-id="element-46" data-mp="9fb2a1v1cguu">
                  {s.nombre}
                </option>)}
            </select>
          </Field>
          <button onClick={addEmp} style={{
        ...primaryButton,
        width: '100%'
      }} data-id="element-47" data-mp="3du143xj222c9">
          
            {editingId ? 'Guardar cambios' : 'Guardar'}
          </button>
        </Modal>}
      {modal === 'medicion' && <Modal title={editingId ? 'Editar medicion' : 'Nueva medicion'} onClose={() => {
      setModal(null);
      setEditingId(null);
    }} data-id="element-48" data-mp="3mw206r2jyicc">
        
          <Field label="SUCURSAL" data-id="element-49" data-mp="x81gkhqevc8a">
            <select style={inputStyle} value={mS} onChange={e => setMS(e.target.value)} data-id="element-50" data-mp="123ytsf6do2gu">
            
              <option value="" data-id="element-51" data-mp="1c7nchtam3ds9">Seleccionar</option>
              {viewSucs.map(s => <option key={s.id} value={s.id} data-id="element-52" data-mp="179vld1hsjvp4">
                  {s.nombre}
                </option>)}
            </select>
          </Field>
          <Field label="PUNTO" data-id="element-53" data-mp="12hklfyn1w9wk">
            <input style={inputStyle} value={mP} onChange={e => setMP(e.target.value)} data-id="element-54" data-mp="12gsookhpw64p" />
          
          </Field>
          <Field label="LUX" data-id="element-55" data-mp="ti7cd72u9ncd">
            <CampoValidado modo="numeros" valor={mL} onChange={setML} placeholder="0" maxLength={6} data-id="element-56" data-mp="tih00o0eview" />
          
          </Field>
          <Field label="EMPLEADO" data-id="element-57" data-mp="ohjbzuspzu5h">
            <select style={inputStyle} value={mE} onChange={e => setME(e.target.value)} data-id="element-58" data-mp="jk1bmssrqn93">
            
              <option value="" data-id="element-59" data-mp="fifsippn5cqg">Seleccionar</option>
              {emps.map(e => <option key={e.id} value={e.id} data-id="element-60" data-mp="fk8w1w4yjgu8">
                  {e.nombre}
                </option>)}
            </select>
          </Field>
          <Field label="NOTAS" data-id="element-61" data-mp="29wqgyx2bq4vp">
            <textarea style={{
          ...inputStyle,
          resize: 'none'
        }} rows={2} value={mN} onChange={e => setMN(e.target.value)} data-id="element-62" data-mp="29xs2xwkvobs7" />
          
          </Field>
          <button onClick={addMed} style={{
        ...primaryButton,
        width: '100%'
      }} data-id="element-63" data-mp="2x722l1e7imft">
          
            {editingId ? 'Guardar cambios' : 'Registrar'}
          </button>
        </Modal>}
      {modal === 'equipo' && <Modal title={editingId ? 'Editar equipo' : 'Nuevo equipo'} onClose={() => {
      setModal(null);
      setEditingId(null);
    }} data-id="element-64" data-mp="2od75mgn8o2b9">
        
          <Field label="NOMBRE" data-id="element-65" data-mp="2eg2juhxb28uz">
            <input style={inputStyle} value={eqNombre} onChange={e => setEqNombre(e.target.value)} data-id="element-66" data-mp="2ehdki8vgf0hv" />
          
          </Field>
          <Field label="SERIAL" data-id="element-67" data-mp="20idxf5nlatnt">
            <CampoValidado modo="codigo" valor={eqSerial} onChange={setEqSerial} data-id="element-68" data-mp="25fcr5n3ccylh" />
          
          </Field>
          <Field label="ESTADO" data-id="element-69" data-mp="3uu13xpfugik1">
            <select style={inputStyle} value={eqEstado} onChange={e => setEqEstado(e.target.value)} data-id="element-70" data-mp="3uvvm4vzqdwkf">
            
              <option data-id="element-71" data-mp="3nht4aa0cslq">Disponible</option>
              <option data-id="element-72" data-mp="dry70y3xfzmw">En uso</option>
              <option data-id="element-73" data-mp="dszmrfvw1aay">Mantenimiento</option>
            </select>
          </Field>
          <Field label="PROXIMA CALIBRACION" data-id="element-74" data-mp="8twgxt83301x">
            <input style={inputStyle} value={eqCal} onChange={e => setEqCal(e.target.value)} data-id="element-75" data-mp="8r0hu1ki25bw" />
          
          </Field>
          <button onClick={addEquipo} style={{
        ...primaryButton,
        width: '100%'
      }} data-id="element-76" data-mp="htt5zvydy0xi">
          
            {editingId ? 'Guardar cambios' : 'Guardar'}
          </button>
        </Modal>}
      {modal === 'rol' && <Modal title={editingId ? 'Editar rol' : 'Nuevo rol'} onClose={() => {
      setModal(null);
      setEditingId(null);
    }} data-id="element-77" data-mp="rke31eyeuxeb">
        
          <Field label="NOMBRE" data-id="element-78" data-mp="15h9rofr5axfh">
            <CampoValidado modo="letras" valor={rolNombre} onChange={setRolNombre} data-id="element-79" data-mp="11i21aev86n18" />
          
          </Field>
          <Field label="DESCRIPCION" data-id="element-80" data-mp="37a7g7zdw33um">
            <input style={inputStyle} value={rolDesc} onChange={e => setRolDesc(e.target.value)} data-id="element-81" data-mp="37b8yantboqlu" />
          
          </Field>
          <div style={{
        fontSize: 11,
        fontWeight: 700,
        color: '#1A1207',
        letterSpacing: '0.08em',
        marginBottom: 8
      }} data-id="element-82" data-mp="33bi40a4l7sck">
          
            PERMISOS
          </div>
          <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        maxHeight: 260,
        overflowY: 'auto',
        marginBottom: 16,
        paddingRight: 4
      }} data-id="element-83" data-mp="1rimludi7k3w8">
          
            {MODULE_KEYS.map(m => {
          const flags = rolPermisos[m] || {
            leer: false,
            editar: false,
            archivar: false
          };
          return <div key={m} style={{
            display: 'grid',
            gridTemplateColumns: '1fr repeat(3,68px)',
            alignItems: 'center',
            gap: 8,
            padding: '8px 10px',
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: 8
          }} data-id="element-84" data-mp="2b6lxrroidh38">
                
                  <span style={{
              fontSize: 12,
              fontWeight: 700,
              color: '#1A1207'
            }} data-id="element-85" data-mp="2fa1sgih0p7da">

                    {MODULE_LABELS[m] ?? m}
                  </span>
                  {/* La tercera acción es 'archivar', que es lo que lee
                   canArchive(). Antes decía 'eliminar', una clave que
                   nadie consulta: el permiso de archivar quedaba
                   imposible de otorgar a un rol no-admin. */}
                  {(['leer', 'editar', 'archivar'] as (keyof PermFlags)[]).map(a => <label key={a} style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              cursor: 'pointer',
              fontSize: 11,
              color: '#1A1207',
              textTransform: 'capitalize'
            }} data-id="element-86" data-mp="2kuhizyvnfz8e">
                    
                        <input type="checkbox" checked={flags[a]} onChange={() => togglePerm(m, a)} data-id="element-87" data-mp="1x682nch7g3on" />
                    

                        {a}
                      </label>)}
                </div>;
        })}
          </div>
          <button onClick={addRol} style={{
        ...primaryButton,
        width: '100%'
      }} data-id="element-88" data-mp="7pujsta5kuc8">
          
            {editingId ? 'Guardar cambios' : 'Guardar'}
          </button>
        </Modal>}
      {modal === 'usuario' && <Modal title={editingId ? 'Editar usuario' : 'Nuevo usuario'} onClose={() => {
      setModal(null);
      setEditingId(null);
    }} data-id="element-89" data-mp="3q20u06014jo1">
        
          <Field label="NOMBRE" data-id="element-90" data-mp="qm3wpd9bjeun">
            <CampoValidado modo="letras" valor={uN} onChange={setUN} placeholder="Nombre y apellido" data-id="element-91" data-mp="qk9iehsjykzj" />
          
          </Field>
          <Field label="CORREO" data-id="element-92" data-mp="hl61nkguyho7">
            <input style={inputStyle} type="text" value={uCorreo} onChange={e => setUCorreo(e.target.value)} placeholder="correo@getr.do" data-id="element-93" data-mp="hke5a7ffsfwv" />
          
          </Field>
          <Field label={editingId ? 'CONTRASEÑA (dejar vacío para mantener)' : 'CONTRASEÑA'} data-id="element-94" data-mp="32cg8pggelabl">
          
            <div style={{
          position: 'relative'
        }} data-id="element-95" data-mp="2xc1z3tz5lsbg">
            
              <input style={{
            ...inputStyle,
            paddingRight: 42
          }} type={showUPass ? 'text' : 'password'} autoComplete="new-password" value={uPass} onChange={e => setUPass(e.target.value)} placeholder="••••••" data-id="element-96" data-mp="1cvknrk00csjc" />
            

              <button type="button" onClick={() => setShowUPass(s => !s)} aria-label={showUPass ? 'Ocultar contraseña' : 'Mostrar contraseña'} style={{
            position: 'absolute',
            right: 6,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'transparent',
            border: 'none',
            color: T.textMuted,
            padding: 6,
            display: 'flex',
            cursor: 'pointer'
          }} data-id="element-97" data-mp="1ltmnlw809f51">
              
                {showUPass ? <EyeOffIcon size={17} data-id="element-98" data-mp="29j7fx5q1toqz" /> : <EyeIcon size={17} data-id="element-99" data-mp="b5yrunabexe8" />}
              </button>
            </div>
          </Field>
          <Field label="ROL" data-id="element-100" data-mp="f7aj0x8u7qoj">
            <select style={inputStyle} value={uRol} onChange={e => setURol(e.target.value as Usuario['rol'])} data-id="element-101" data-mp="f8bxzcsz4xbj">
            
              {roles.length === 0 && <option value="" disabled data-id="element-102" data-mp="3g4kyyyctn4ss">
                  No hay roles en la base de datos
                </option>}
              {roles.map(r => <option key={r.id} value={r.id.replace(/^rol-/, '')} data-id="element-103" data-mp="3q17klpcz1zvv">
                  {r.nombre}
                </option>)}
            </select>
          </Field>
          {uRol !== 'admin' && <Field label="PUNTOS COMERCIALES ASIGNADOS" data-id="element-104" data-mp="2xc0ea47a0602">
              <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6
        }} data-id="element-105" data-mp="2xcjt3c1mqe9v">
            
                {sucs.map(s => <label key={s.id} style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            fontSize: 13,
            color: T.textSecondary,
            padding: '6px 8px',
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: 8
          }} data-id="element-106" data-mp="371jxxc2tc3vz">
              
                    <input type="checkbox" checked={uSucs.includes(s.id)} onChange={() => toggleUSuc(s.id)} data-id="element-107" data-mp="p4donjhu8lzr" />
              

                    {s.nombre}
                  </label>)}
              </div>
            </Field>}
          {uRol === 'admin' && <div style={{
        fontSize: 12,
        color: '#c4b5fd',
        marginBottom: 16
      }} data-id="element-108" data-mp="g4qnmyt0ti78">
          
              El administrador tiene acceso a todos los puntos comerciales.
            </div>}
          <button onClick={addUsuario} style={{
        ...primaryButton,
        width: '100%'
      }} data-id="element-109" data-mp="17vekvzrkq3fm">
          
            {editingId ? 'Guardar cambios' : 'Crear usuario'}
          </button>
        </Modal>}
      {dialogo}
    </div>;
}